import React, { useState, useEffect } from 'react'
import { 
    Box, 
    Paper, 
    Typography, 
    CircularProgress, 
    Alert, 
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Card,
    CardContent
} from '@mui/material'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { getAllHistogramData } from '../../../../../apiRoutes'

const theme = createTheme({
    palette: {
        mode: 'dark',
        primary: { main: '#8cc63f' },
        secondary: { main: '#4caf50' },
        background: { default: '#1a1a1a', paper: '#2d2d2d' },
        text: { primary: '#ffffff', secondary: '#b0b0b0' },
    },
})

interface CCPViewProps {
    row: {
        deviceSerial?: string | any
        device?: string | any
    }
}

interface HistogramData {
    deviceId: string
    timestamp: number
    x_headers: string[]
    y_headers: string[]
    records: number[][]
}

interface HistogramTableProps {
    title: string
    data: HistogramData | null
    loading: boolean
    error: string | null
    histogramType?: 'ect' | 'speedload' | 'knock'
}

const HistogramTable: React.FC<HistogramTableProps> = ({ title, data, loading, error, histogramType }) => {
    if (loading) {
        return (
            <Card sx={{ height: '100%' }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                        {title}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                            Loading {title.toLowerCase()} data...
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        )
    }

    if (error) {
        return (
            <Card sx={{ height: '100%' }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                        {title}
                    </Typography>
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                </CardContent>
            </Card>
        )
    }

    if (!data) {
        return (
            <Card sx={{ height: '100%' }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                        {title}
                    </Typography>
                    <Alert severity="info" sx={{ mt: 2 }}>
                        No {title.toLowerCase()} data available
                    </Alert>
                </CardContent>
            </Card>
        )
    }

    const formatTimestamp = (timestamp: number) => {
        const seconds = Math.floor(timestamp / 1000)
        const milliseconds = timestamp % 1000
        return new Date(seconds * 1000 + milliseconds).toLocaleString()
    }

    return (
        <Card sx={{ width: 'fit-content' }}>
            <CardContent>
                <Typography variant="h6" sx={{ color: 'primary.main', mb: 2 }}>
                    {title}
                </Typography>

                <TableContainer component={Paper} sx={{ backgroundColor: 'background.paper', width: 'fit-content' }}>
                    <Table size="small" sx={{ width: 'auto' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ color: 'text.primary', fontWeight: 'bold', whiteSpace: 'nowrap',  backgroundColor: 'background.default',}}>
                                    {histogramType === 'ect' ? (data.y_headers[0] || 'Y-Axis') : `${data.y_headers[0]}/${data.x_headers[0]}`}
                                </TableCell>
                                {histogramType === 'ect' ? (
                                    data.x_headers.map((header, index) => (
                                        <TableCell 
                                            key={index} 
                                            sx={{ 
                                                color: 'text.primary',
                                                backgroundColor: 'background.default',
                                                fontWeight: 'bold',
                                                textAlign: 'center',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {header}
                                        </TableCell>
                                    ))
                                ) : (
                                    data.x_headers.slice(1).map((header, index) => (
                                        <TableCell 
                                            key={index} 
                                            sx={{ 
                                                color: 'text.primary', 
                                                backgroundColor: 'background.default',
                                                fontWeight: 'bold',
                                                textAlign: 'center',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {header}
                                        </TableCell>
                                    ))
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.records.map((row, rowIndex) => (
                                <TableRow key={rowIndex}>
                                    <TableCell 
                                        sx={{ 
                                            backgroundColor: 'background.default', 
                                            color: 'text.primary',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        {data.y_headers[rowIndex + 1] || `${rowIndex + 1}`}
                                    </TableCell>
                                    {row.map((cell, cellIndex) => (
                                        <TableCell 
                                            key={cellIndex} 
                                            sx={{ 
                                                backgroundColor: 'background.paper', 
                                                color: 'text.primary',
                                                textAlign: 'center'
                                            }}
                                        >
                                            {cell}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Updated: {formatTimestamp(data.timestamp)}
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    )
}

const CCPView: React.FC<CCPViewProps> = ({ row }) => {
    const [histogramData, setHistogramData] = useState<{
        ect: HistogramData | null
        speedload: HistogramData | null
        knock: HistogramData | null
    }>({
        ect: null,
        speedload: null,
        knock: null
    })
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        const fetchHistogramData = async () => {
            let deviceSerial: string | null = null

            // Try to get deviceSerial from various possible locations
            if (typeof row.deviceSerial === 'string') {
                deviceSerial = row.deviceSerial
            } else if (typeof row.device === 'string') {
                deviceSerial = row.device
            } else if (row.deviceSerial && typeof row.deviceSerial === 'object') {
                const deviceObj = row.deviceSerial as any
                deviceSerial = deviceObj.deviceSerial || deviceObj.device || deviceObj._id || deviceObj.id
            }

            if (!deviceSerial) {
                setError('No device serial found for fetching histogram data')
                return
            }

            setLoading(true)
            setError(null)

            try {
                const response = await getAllHistogramData(deviceSerial)
                
                if (response?.success && response?.data) {
                    setHistogramData({
                        ect: response.data.ect,
                        speedload: response.data.speedload,
                        knock: response.data.knock
                    })
                } else {
                    setError(response?.error || 'Failed to fetch histogram data')
                }
            } catch (err: any) {
                console.error('Error fetching histogram data:', err)
                setError(err.message || 'Failed to fetch histogram data')
            } finally {
                setLoading(false)
            }
        }

        fetchHistogramData()
    }, [row.deviceSerial, row.device])

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ width: '100%', bgcolor: 'background.default', minHeight: '50vh' }}>
                <Box sx={{ p: 3 }}>
                    {/* <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', mb: 3 }}>
                        Service
                    </Typography> */}
                    
                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {error}
                        </Alert>
                    )}

                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'row', 
                        gap: 2,
                        overflowX: 'auto'
                    }}>
                        <Box sx={{ flexShrink: 0 }}>
                            <HistogramTable
                                title="Speed-Load Histogram"
                                data={histogramData.speedload}
                                loading={loading}
                                error={error}
                                histogramType="speedload"
                            />
                        </Box>
                        <Box sx={{ flexShrink: 0 }}>
                            <HistogramTable
                                title="ECT Histogram"
                                data={histogramData.ect}
                                loading={loading}
                                error={error}
                                histogramType="ect"
                            />
                        </Box>
                        <Box sx={{ flexShrink: 0 }}>
                            <HistogramTable
                                title="Knock Histogram"
                                data={histogramData.knock}
                                loading={loading}
                                error={error}
                                histogramType="knock"
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>
        </ThemeProvider>
    )
}

export default CCPView
